import { reservationMoneyPreview, missingReservationRateText } from '../utils/reservationMoney';
import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { utcISOToParisDate, getLocaleByLang } from '../utils/timezone';
import { DateTime } from 'luxon';
// html2canvas - 현재 사용되지 않음
// import html2canvas from 'html2canvas';
import styles from './ManualReservationList.module.css';
// eslint-disable-next-line no-unused-vars
import { modalStyles, modalContentStyles, modalActionStyles } from '../utils/styleUtils';
// eslint-disable-next-line no-unused-vars
import { checkTokenAndLogout } from '../utils/auth';
import { canDownloadFacture } from '../utils/permissionConfig';
import { CURRENCY_OPTIONS, formatCurrency, fetchExchangeRates } from '../utils/currency';
import { ManualReservationImageGallery } from './ReservationImageGallery';
import DepositList from './DepositList';
// eslint-disable-next-line no-unused-vars
import CsvPreviewModal from './CsvPreviewModal';
import ReservationTypeSelector, { getReservationTypeLabel } from './ReservationTypeSelector';
import { MODERN_THEME } from '../styles/themes';
import { TEXT } from './manualReservationText';
import {
  buildAccountingPreviewData,
  downloadAccountingCsv,
  downloadAccountingFacturePdf
} from './manualReservationAccounting';
import { loadAccountingInvoiceReferences } from '../services/accountingInvoiceReferenceApi';
import {
  downloadManualReservationsCsv,
  formatRevenueLabel as formatRevenueAmountLabel,
  getClosestFutureReservationDate,
  getMonthlyDepositTotal as calculateMonthlyDepositTotal,
  getMonthlyRevenueTotal as calculateMonthlyRevenueTotal,
  getYearlyDepositTotal as calculateYearlyDepositTotal,
  getYearlyRevenueTotal as calculateYearlyRevenueTotal,
  sortAndFilterManualReservations
} from './manualReservationListUtils';
import {
  buildFinalPaymentToggleEditData,
  buildInitialManualReservationEditData,
  buildManualReservationDepositReceivedDate,
  getInitialManualReservationDateInputValue,
  getManualReservationRemainingAmount,
  mergeUpdatedManualReservation
} from './manualReservationModalState';
import {
  deleteManualReservationById,
  deleteManualReservationWithEventById,
  fetchManualReservationsForUser,
  updateManualReservationById
} from '../services/manualReservationApi';
import { getReservationPrimaryImageUrl } from '../utils/reservationImageUrl';

// React Query 훅 (향후 마이그레이션용)
// eslint-disable-next-line no-unused-vars
import { useApiQuery } from '../lib/useApiQuery';

import {
  getSearchFormStyles,
  getPageContainerStyles,
  getTableStyles,
  getStatusFilterStyles,
  getLoadingStyles,
  getStatusStyles,
  getModalStyles,
  getModalContentStyles,
  getModalActionStyles
} from '../utils/styleUtils';
/* eslint-disable no-unused-vars */
import {
  formatUTCToLocalDate,
  formatUTCToLocalDateSlash,
  formatUTCToLocalTime,
  formatLocalToUTC,
  formatDateTimeRange,
  createLocalDateTimeInfo
} from '../utils/timezone';
/* eslint-enable no-unused-vars */

function ManualReservationList() {
  const { userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // userInfo가 로드되기 전까지는 기본값 사용
  const lang = userInfo?.preferred_language || 'ko';
  const canExportFacture = canDownloadFacture(userInfo);

  // 안전한 초기화를 위한 기본값 설정 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const safeUserInfo = userInfo || {};
  // eslint-disable-next-line no-unused-vars
  const searchStyles = getSearchFormStyles();
  const pageContainerStyles = getPageContainerStyles();
  const tableStyles = getTableStyles();
  // eslint-disable-next-line no-unused-vars
  const statusFilterStyles = getStatusFilterStyles();
  const loadingStyles = getLoadingStyles();
  // eslint-disable-next-line no-unused-vars
  const statusStyles = getStatusStyles();
  const modalStyles = getModalStyles();
  const modalContentStyles = getModalContentStyles();
  const modalActionStyles = getModalActionStyles();

  // 수동예약 검색/필터 상태
  const [manualSearch, setManualSearch] = useState('');
  const [manualCategory, setManualCategory] = useState('client_name');
  const [manualStatus, setManualStatus] = useState('all');
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  // eslint-disable-next-line no-unused-vars
  const [manualBtnActive, setManualBtnActive] = useState(false);
  const [sortField, setSortField] = useState('event_start_time');
  const [sortOrder, setSortOrder] = useState('desc');

  // 수동예약정보 데이터 상태
  const [manualReservations, setManualReservations] = useState([]);
  const [loadingManualReservations, setLoadingManualReservations] = useState(false);
  const [exchangeRates, setExchangeRates] = useState(null);

  // 수동예약정보 모달 상태
  const [manualReservationModalOpen, setManualReservationModalOpen] = useState(false);
  const [selectedManualReservation, setSelectedManualReservation] = useState(null);
  const [isEditingManualReservation, setIsEditingManualReservation] = useState(false);
  const [editManualReservationData, setEditManualReservationData] = useState({});

  // 입력값 관리를 위한 별도 상태
  const [dateInputValue, setDateInputValue] = useState('');

  // 강제 리렌더링을 위한 상태 - 메모리 누수 방지를 위해 제거
  // const [forceRenderKey, setForceRenderKey] = useState(0);

  // 삭제 선택 모달 상태
  const [deleteChoiceModalOpen, setDeleteChoiceModalOpen] = useState(false);

  // 뷰 모드 상태 (table 또는 card)
  const [viewMode, setViewMode] = useState('card');

  // 탭 상태 (reservations 또는 deposits)
  const [activeTab, setActiveTab] = useState('reservations');

  // CSV 미리보기 모달 상태
  const [csvPreviewModalOpen, setCsvPreviewModalOpen] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState([]);

  // 스크롤 피커 ref
  const yearPickerRef = useRef(null);
  const monthPickerRef = useRef(null);
  const ignoreYearScrollRef = useRef(false);
  const ignoreMonthScrollRef = useRef(false);
  const userYearScrollIntentRef = useRef(false);
  const userMonthScrollIntentRef = useRef(false);
  const yearScrollUnlockTimeoutRef = useRef(null);
  const monthScrollUnlockTimeoutRef = useRef(null);
  const yearScrollEndTimeoutRef = useRef(null);
  const monthScrollEndTimeoutRef = useRef(null);
  const yearAlignRetryTimeoutRef = useRef(null);
  const monthAlignRetryTimeoutRef = useRef(null);

  // 스크롤 피커 - 연도 목록 생성 (현재 년도 기준 ±5년)
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const years = useMemo(() => Array.from({ length: 11 }, (_, i) => currentYear - 5 + i), [currentYear]);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // 스크롤 피커 - 해당 값을 중앙으로 스크롤
  const scheduleScrollUnlock = useCallback((ignoreScrollRef, timeoutRef, delay = 80) => {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      ignoreScrollRef.current = false;
    }, delay);
  }, []);

  const findClosestIndex = useCallback((ref) => {
    if (!ref.current) return -1;
    const container = ref.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestIndex = -1;
    let closestDistance = Infinity;

    Array.from(container.children).forEach((item, idx) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(containerCenter - itemCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });

    return closestIndex;
  }, []);

  const scrollToCenter = useCallback((ref, index, options = {}) => {
    if (!ref.current) return;
    const container = ref.current;
    const items = container.children;
    if (index < 0 || index >= items.length) return;
    const {
      behavior = 'smooth',
      ignoreScrollRef = null,
      unlockTimeoutRef = null,
      userScrollIntentRef = null,
      scrollEndTimeoutRef = null,
      alignRetryTimeoutRef = null
    } = options;

    const item = items[index];
    const scrollPos = Math.max(0, item.offsetLeft - (container.clientWidth / 2) + (item.offsetWidth / 2));

    if (scrollEndTimeoutRef) {
      window.clearTimeout(scrollEndTimeoutRef.current);
    }

    // 아이템이 중앙에 오도록 스크롤
    if (ignoreScrollRef && unlockTimeoutRef) {
      ignoreScrollRef.current = true;
      scheduleScrollUnlock(ignoreScrollRef, unlockTimeoutRef, behavior === 'smooth' ? 420 : 220);
    }
    if (userScrollIntentRef) {
      userScrollIntentRef.current = false;
    }
    if (alignRetryTimeoutRef) {
      window.clearTimeout(alignRetryTimeoutRef.current);
    }

    item.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
    container.scrollTo({ left: scrollPos, behavior });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        item.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
        container.scrollTo({ left: scrollPos, behavior: 'auto' });
      });
    });

    if (alignRetryTimeoutRef) {
      alignRetryTimeoutRef.current = window.setTimeout(() => {
        item.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
        container.scrollTo({ left: scrollPos, behavior: 'auto' });
      }, behavior === 'smooth' ? 240 : 140);
    }
  }, [scheduleScrollUnlock]);

  const markUserScrollIntent = useCallback((intentRef, endTimeoutRef) => {
    intentRef.current = true;
    window.clearTimeout(endTimeoutRef.current);
  }, []);

  const clearPickerTimeouts = useCallback(() => {
    [
      yearScrollUnlockTimeoutRef,
      monthScrollUnlockTimeoutRef,
      yearScrollEndTimeoutRef,
      monthScrollEndTimeoutRef,
      yearAlignRetryTimeoutRef,
      monthAlignRetryTimeoutRef
    ].forEach(timeoutRef => {
      window.clearTimeout(timeoutRef.current);
    });
  }, []);

  // 스크롤 이벤트 핸들러 - 중앙 아이템 감지
  const handleYearScroll = () => {
    if (!yearPickerRef.current || ignoreYearScrollRef.current || !userYearScrollIntentRef.current) return;

    window.clearTimeout(yearScrollEndTimeoutRef.current);
    yearScrollEndTimeoutRef.current = window.setTimeout(() => {
      if (!userYearScrollIntentRef.current) return;

      const closestIndex = findClosestIndex(yearPickerRef);
      userYearScrollIntentRef.current = false;

      const newYear = years[closestIndex];
      if (newYear !== undefined && newYear !== selectedYear) {
        setSelectedYear(newYear);
      }
    }, 120);
  };

  const handleMonthScroll = () => {
    if (!monthPickerRef.current || ignoreMonthScrollRef.current || !userMonthScrollIntentRef.current) return;

    window.clearTimeout(monthScrollEndTimeoutRef.current);
    monthScrollEndTimeoutRef.current = window.setTimeout(() => {
      if (!userMonthScrollIntentRef.current) return;

      const closestIndex = findClosestIndex(monthPickerRef);
      userMonthScrollIntentRef.current = false;

      const newMonth = months[closestIndex];
      if (newMonth !== undefined && newMonth !== selectedMonth) {
        setSelectedMonth(newMonth);
      }
    }, 120);
  };

  // 연도별 총 매출 계산 (유로 환산 적용)
  const getYearlyRevenue = (year) => {
    return calculateYearlyRevenueTotal(manualReservations, year, exchangeRates);
  };

  // 월별 총 매출 계산 (선택된 연도 기준, 유로 환산 적용)
  const getMonthlyRevenue = (month) => {
    return calculateMonthlyRevenueTotal(manualReservations, selectedYear, month, exchangeRates);
  };

  // 연도별 선금 합계 계산 (선금 받은 날짜 기준)
  const getYearlyDepositTotal = (year) => {
    return calculateYearlyDepositTotal(manualReservations, year, exchangeRates);
  };

  // 월별 선금 합계 계산 (선금 받은 날짜 기준)
  const getMonthlyDepositTotal = (month) => {
    return calculateMonthlyDepositTotal(manualReservations, selectedYear, month, exchangeRates);
  };

  // 금액 포맷 (1000 -> 1k, 1000000 -> 1M) - 소수점 첫째자리까지
  const formatRevenue = (amount) => {
    return formatRevenueAmountLabel(amount);
  };

  // 마운트시 선택된 값으로 스크롤
  useEffect(() => {
    const timer = setTimeout(() => {
      const yearIndex = years.indexOf(selectedYear);
      const monthIndex = months.indexOf(selectedMonth);
      scrollToCenter(yearPickerRef, yearIndex, {
        behavior: 'auto',
        ignoreScrollRef: ignoreYearScrollRef,
        unlockTimeoutRef: yearScrollUnlockTimeoutRef,
        userScrollIntentRef: userYearScrollIntentRef,
        scrollEndTimeoutRef: yearScrollEndTimeoutRef,
        alignRetryTimeoutRef: yearAlignRetryTimeoutRef
      });
      scrollToCenter(monthPickerRef, monthIndex, {
        behavior: 'auto',
        ignoreScrollRef: ignoreMonthScrollRef,
        unlockTimeoutRef: monthScrollUnlockTimeoutRef,
        userScrollIntentRef: userMonthScrollIntentRef,
        scrollEndTimeoutRef: monthScrollEndTimeoutRef,
        alignRetryTimeoutRef: monthAlignRetryTimeoutRef
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [months, scrollToCenter, selectedMonth, selectedYear, years]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    if (!yearPickerRef.current && !monthPickerRef.current) return undefined;

    const observer = new ResizeObserver(() => {
      const yearIndex = years.indexOf(selectedYear);
      const monthIndex = months.indexOf(selectedMonth);

      if (yearIndex !== -1) {
        scrollToCenter(yearPickerRef, yearIndex, {
          behavior: 'auto',
          ignoreScrollRef: ignoreYearScrollRef,
          unlockTimeoutRef: yearScrollUnlockTimeoutRef,
          userScrollIntentRef: userYearScrollIntentRef,
          scrollEndTimeoutRef: yearScrollEndTimeoutRef,
          alignRetryTimeoutRef: yearAlignRetryTimeoutRef
        });
      }

      if (monthIndex !== -1) {
        scrollToCenter(monthPickerRef, monthIndex, {
          behavior: 'auto',
          ignoreScrollRef: ignoreMonthScrollRef,
          unlockTimeoutRef: monthScrollUnlockTimeoutRef,
          userScrollIntentRef: userMonthScrollIntentRef,
          scrollEndTimeoutRef: monthScrollEndTimeoutRef,
          alignRetryTimeoutRef: monthAlignRetryTimeoutRef
        });
      }
    });

    if (yearPickerRef.current) observer.observe(yearPickerRef.current);
    if (monthPickerRef.current) observer.observe(monthPickerRef.current);

    return () => observer.disconnect();
  }, [months, scrollToCenter, selectedMonth, selectedYear, years]);

  useEffect(() => clearPickerTimeouts, [clearPickerTimeouts]);

  // 환율 데이터 로드
  useEffect(() => {
    fetchExchangeRates().then(rates => {
      setExchangeRates(rates);
    });
  }, []);

  // deposit_received 상태 변화 감지 및 강제 리렌더링 - 메모리 누수 방지를 위해 제거
  // useEffect(() => {
  //   if (editManualReservationData.deposit_received !== undefined) {
  //     console.log('deposit_received 상태 변화 감지:', editManualReservationData.deposit_received);
  //     setForceRenderKey(prev => prev + 1);
  //   }
  // }, [editManualReservationData.deposit_received]);

  // 수동예약 검색 함수
  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualSearch.trim() || manualCategory !== 'client_name') {
      setManualBtnActive(true);
      setTimeout(() => setManualBtnActive(false), 1000);
    } else {
      setManualBtnActive(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // 가장 가까운 미래 날짜 찾기
  const getClosestFutureDate = () => {
    return getClosestFutureReservationDate(manualReservations);
  };

  // 수동예약 데이터 정렬 및 필터링 함수
  const getSortedManualReservations = () => {
    return sortAndFilterManualReservations(manualReservations, {
      manualSearch,
      manualCategory,
      manualStatus,
      selectedYear,
      selectedMonth,
      sortField,
      sortOrder
    });
  };

  // 수동예약정보 모달 열기/닫기
  const openManualReservationModal = (reservation) => {
    setSelectedManualReservation(reservation);
    setEditManualReservationData(buildInitialManualReservationEditData(reservation));
    setDateInputValue(getInitialManualReservationDateInputValue(reservation));

    setIsEditingManualReservation(false);
    setManualReservationModalOpen(true);
  };

  const closeManualReservationModal = () => {
    setManualReservationModalOpen(false);
    setSelectedManualReservation(null);
    setIsEditingManualReservation(false);
    setEditManualReservationData({});
    setDateInputValue('');
    setDeleteChoiceModalOpen(false);
  };

  // 수동예약 수정 모드 토글
  const toggleEditMode = () => {
    setIsEditingManualReservation(!isEditingManualReservation);
  };

  const updateEditManualReservationField = (field, value) => {
    setEditManualReservationData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'currency' ? { update_exchange_rate: true } : {})
    }));
  };

  const resizeManualReservationTextarea = (element) => {
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  // 수동예약만 삭제 (일정 유지)
  const deleteManualReservationOnly = async () => {
    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    try {
      await deleteManualReservationById(selectedManualReservation.id);
      alert(TEXT.manualReservationDeleted[lang]);
      closeManualReservationModal();
      setTimeout(() => {
        if (typeof fetchManualReservations === 'function') {
          fetchManualReservations();
        }
      }, 0);
    } catch (error) {
      console.error('삭제 에러:', error);
      alert('삭제 중 오류가 발생했습니다: ' + error.message);
    }
  };

  // 수동예약과 일정을 모두 삭제
  const deleteManualReservationWithEvent = async () => {
    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    try {
      await deleteManualReservationWithEventById(selectedManualReservation.id);
      alert(TEXT.deleteWithEventSuccess[lang]);
      closeManualReservationModal();
      setTimeout(() => {
        if (typeof fetchManualReservations === 'function') {
          fetchManualReservations();
        }
      }, 0);
    } catch (error) {
      console.error('삭제 에러:', error);
      alert('삭제 중 오류가 발생했습니다: ' + error.message);
    }
  };

  // 수동예약 수정 저장
  const handleManualReservationSave = async () => {
    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    try {
      const result = await updateManualReservationById(
        selectedManualReservation.id,
        editManualReservationData,
        exchangeRates
      );

      alert(TEXT.saveSuccess[lang]);
      setIsEditingManualReservation(false);

      if (result.manual_reservation) {
        setSelectedManualReservation(prev => mergeUpdatedManualReservation(prev, result.manual_reservation));
      }

      fetchManualReservations();
    } catch (error) {
      console.error('수동예약 수정 에러:', error);
      alert('수정 중 오류가 발생했습니다: ' + error.message);
    }
  };

  // CSV 다운로드 함수
  const downloadCSV = () => {
    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    const sortedReservations = getSortedManualReservations();
    if (sortedReservations.length === 0) {
      alert('No data to download');
      return;
    }
    downloadManualReservationsCsv(sortedReservations);
  };

  // 회계용 CSV 미리보기 열기 (예약용)
  const openAccountingCsvPreview = async () => {
    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    const sortedReservations = getSortedManualReservations();
    if (sortedReservations.length === 0) {
      alert('No data to download');
      return;
    }

    const previewData = buildAccountingPreviewData(sortedReservations, selectedYear, selectedMonth);

    if (previewData.length === 0) {
      alert(lang === 'ko' ? '결제 완료된 예약이 없습니다' : lang === 'fr' ? 'Aucune réservation payée' : 'No paid reservations');
      return;
    }

    try {
      setCsvPreviewData(await loadAccountingInvoiceReferences(previewData));
      setCsvPreviewModalOpen(true);
    } catch (error) {
      console.error('Failed to load tattoo invoice references:', error);
      alert(lang === 'ko' ? '팩처 번호를 불러오지 못했습니다.' : lang === 'fr' ? 'Impossible de charger les numéros de facture.' : 'Could not load invoice numbers.');
    }
  };

  // CSV 다운로드 핸들러 (CsvPreviewModal 콜백)
  const handleCsvDownload = (selectedData) => {
    if (selectedData.length === 0) {
      alert(lang === 'ko' ? '선택된 항목이 없습니다' : lang === 'fr' ? 'Aucune sélection' : 'No selection');
      return;
    }
    downloadAccountingCsv(selectedData, selectedYear, selectedMonth);
  };

  // Facture PDF 다운로드 핸들러 (CsvPreviewModal 콜백)
  const handleFactureDownload = (selectedData) => {
    // 권한 체크
    if (!canExportFacture) {
      return;
    }

    if (selectedData.length === 0) {
      return;
    }

    downloadAccountingFacturePdf(selectedData, selectedYear, selectedMonth);
  };

  // 수동예약정보 데이터 가져오기
  const fetchManualReservations = useCallback(async () => {
    if (!userInfo?.user_id) {
      return;
    }

    // 토큰 만료 체크
    if (checkTokenAndLogout()) {
      return;
    }

    setLoadingManualReservations(true);
    try {
      const manualReservationsData = await fetchManualReservationsForUser({
        userId: userInfo.user_id,
        userLevel: userInfo.level
      });

      if (!manualReservationsData) {
        // API 호출 실패 (토큰 만료 등으로 인한 자동 로그아웃 처리됨)
        return;
      }

      // 백엔드 최적화 적용: 이미 relatedEvent가 포함되어 있으므로 추가 호출 불필요
      // N+1 문제 해결됨
      setManualReservations(manualReservationsData);
    } catch (error) {
      console.error('수동예약정보 로드 에러:', error);
      setManualReservations([]);
    } finally {
      setLoadingManualReservations(false);
    }
  }, [userInfo?.level, userInfo?.user_id]);

  // 테마 설정 (중앙화된 테마 사용)
  const currentTheme = MODERN_THEME;

  // 테마에 맞게 PWA 노치 여백 배경색 설정
  useEffect(() => {
    // theme-color 메타 태그 업데이트
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentTheme.backgroundColor);
    }

    // body 배경색도 테마에 맞게 변경 (PWA 노치 여백 포함)
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = currentTheme.backgroundColor;

    // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8'); // 오프화이트로 복원
      }
      document.body.style.backgroundColor = originalBodyBg;
    };
  }, [currentTheme.backgroundColor]);

  // 컴포넌트 마운트 시 토큰 검사 및 데이터 로드
  useEffect(() => {
    // 페이지 로드 시 즉시 토큰 검사
    if (checkTokenAndLogout() || !userInfo?.user_id) {
      return;
    }

    // 약간의 지연 후 데이터 로드 (컴포넌트 마운트 완료 후)
    const timeoutId = setTimeout(() => {
      fetchManualReservations();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [fetchManualReservations, userInfo?.user_id]);

  // 삭제 선택 모달 열림/닫힘 시 body 스크롤 제어 - 제거
  // useEffect(() => {
  //   if (deleteChoiceModalOpen) {
  //     // 모달이 열릴 때 body 스크롤 막기
  //     document.body.style.overflow = 'hidden';
  //     document.body.style.paddingRight = '0px';
  //   } else {
  //     // 모달이 닫힐 때 body 스크롤 복원
  //     document.body.style.overflow = '';
  //     document.body.style.paddingRight = '';
  //   }

  //   // cleanup 함수: 컴포넌트 언마운트 시 스크롤 복원
  //   return () => {
  //     document.body.style.overflow = '';
  //     document.body.style.paddingRight = '';
  //   };
  // }, [deleteChoiceModalOpen]);



  // 수동예약 모달 열림/닫힘 시 body 스크롤 제어 - 제거
  // useEffect(() => {
  //   if (manualReservationModalOpen) {
  //     // 모달이 열릴 때 body 스크롤만 막기
  //     document.body.style.overflow = 'hidden';
  //     document.body.style.paddingRight = '0px';
  //   } else {
  //     // 모달이 닫힐 때 body 스크롤 복원
  //     document.body.style.overflow = '';
  //     document.body.style.paddingRight = '';
  //   }

  //   // cleanup 함수: 컴포넌트 언마운트 시 스크롤 복원
  //   return () => {
  //     document.body.style.overflow = '';
  //     document.body.style.paddingRight = '';
  //   };
  // }, [manualReservationModalOpen]);

  // 토큰 만료 체크 (컴포넌트 렌더링 전)
  if (checkTokenAndLogout()) {
    return <div style={{ padding: '3px', textAlign: 'center' }}>Redirecting...</div>;
  }

  if (!userInfo?.user_id) {
    return <div style={{ padding: '3px', textAlign: 'center' }}>Loading user info...</div>;
  }

  return (
    <div className={pageContainerStyles.pageContainer} style={{ height: '100%', overflow: 'auto' }}>
      <h1 className={pageContainerStyles.pageTitle}>{TEXT.pageTitle[lang]}</h1>

      {/* 탭 헤더 */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '12px',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('reservations')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'reservations' ? '2px solid #ff4d8b' : '2px solid transparent',
            color: activeTab === 'reservations' ? '#ff4d8b' : '#666',
            fontWeight: activeTab === 'reservations' ? '500' : '400',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {lang === 'ko' ? '예약 리스트' : lang === 'fr' ? 'Réservations' : 'Reservations'}
        </button>
        <button
          onClick={() => setActiveTab('deposits')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'deposits' ? '2px solid #4caf50' : '2px solid transparent',
            color: activeTab === 'deposits' ? '#4caf50' : '#666',
            fontWeight: activeTab === 'deposits' ? '500' : '400',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {lang === 'ko' ? '선금 리스트' : lang === 'fr' ? 'Acomptes' : 'Deposits'}
        </button>
      </div>

      {/* 년도/월 스크롤 피커 (양 탭 공용) */}
      <div className={styles.scrollPickerContainer} style={{ gap: '2px' }}>
        {/* 연도 스크롤 피커 */}
        <div className={styles.scrollPickerWrapper}>
          <div
            ref={yearPickerRef}
            className={styles.scrollPicker}
            onScroll={handleYearScroll}
            onPointerDown={() => markUserScrollIntent(userYearScrollIntentRef, yearScrollEndTimeoutRef)}
            onTouchStart={() => markUserScrollIntent(userYearScrollIntentRef, yearScrollEndTimeoutRef)}
            onWheel={() => markUserScrollIntent(userYearScrollIntentRef, yearScrollEndTimeoutRef)}
          >
            {years.map(year => (
              <div
                key={year}
                className={`${styles.scrollPickerItem} ${selectedYear === year ? styles.scrollPickerItemActive : ''}`}
                onClick={() => {
                  const idx = years.indexOf(year);
                  setSelectedYear(year);
                  scrollToCenter(yearPickerRef, idx, {
                    behavior: 'auto',
                    ignoreScrollRef: ignoreYearScrollRef,
                    unlockTimeoutRef: yearScrollUnlockTimeoutRef,
                    userScrollIntentRef: userYearScrollIntentRef,
                    scrollEndTimeoutRef: yearScrollEndTimeoutRef,
                    alignRetryTimeoutRef: yearAlignRetryTimeoutRef
                  });
                }}
              >
                {year}
                <div className={styles.scrollPickerRevenue}>
                  {formatRevenue(activeTab === 'deposits' ? getYearlyDepositTotal(year) : getYearlyRevenue(year))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 월 스크롤 피커 */}
        <div className={styles.scrollPickerWrapper}>
          <div
            ref={monthPickerRef}
            className={`${styles.scrollPicker} ${styles.scrollPickerMonth}`}
            onScroll={handleMonthScroll}
            onPointerDown={() => markUserScrollIntent(userMonthScrollIntentRef, monthScrollEndTimeoutRef)}
            onTouchStart={() => markUserScrollIntent(userMonthScrollIntentRef, monthScrollEndTimeoutRef)}
            onWheel={() => markUserScrollIntent(userMonthScrollIntentRef, monthScrollEndTimeoutRef)}
          >
            {months.map(month => (
              <div
                key={month}
                className={`${styles.scrollPickerItem} ${selectedMonth === month ? styles.scrollPickerItemActive : ''}`}
                onClick={() => {
                  const idx = months.indexOf(month);
                  setSelectedMonth(month);
                  scrollToCenter(monthPickerRef, idx, {
                    behavior: 'auto',
                    ignoreScrollRef: ignoreMonthScrollRef,
                    unlockTimeoutRef: monthScrollUnlockTimeoutRef,
                    userScrollIntentRef: userMonthScrollIntentRef,
                    scrollEndTimeoutRef: monthScrollEndTimeoutRef,
                    alignRetryTimeoutRef: monthAlignRetryTimeoutRef
                  });
                }}
              >
                {month}
                <div className={styles.scrollPickerRevenue}>
                  {formatRevenue(activeTab === 'deposits' ? getMonthlyDepositTotal(month) : getMonthlyRevenue(month))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {manualReservations.some(r => r.accounting?.missing_rate) && <p role="alert">{missingReservationRateText[lang] || missingReservationRateText.en}</p>}
      {/* 선금 리스트 탭 */}
      {activeTab === 'deposits' && (
        <DepositList
          reservations={manualReservations}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          exchangeRates={exchangeRates}
          lang={lang}
          loading={loadingManualReservations}
          onOpenModal={openManualReservationModal}
          userInfo={userInfo}
        />
      )}

      {/* 예약 리스트 탭 */}
      {activeTab === 'reservations' && (
        <>
          {/* 검색 폼과 상태 필터 */}
          <form onSubmit={handleManualSearch} style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative', minWidth: '120px' }}>
              <div style={{
                backgroundColor: 'transparent',
                color: '#000',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '3px 12px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '28px',
                boxSizing: 'border-box'
              }}>
                <span>
                  {manualCategory === 'client_name' ? (lang === 'ko' ? '이름' : lang === 'fr' ? 'Nom' : 'Name') :
                    manualCategory === 'client_instagram' ? (lang === 'ko' ? '인스타그램' : lang === 'fr' ? 'Instagram' : 'Instagram') :
                      manualCategory === 'memo' ? (lang === 'ko' ? '메모' : lang === 'fr' ? 'Mémo' : 'Memo') : ''}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
              </div>
              <select
                value={manualCategory}
                onChange={e => setManualCategory(e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '14px'
                }}
              >
                <option value="client_name">{lang === 'ko' ? '이름' : lang === 'fr' ? 'Nom' : 'Name'}</option>
                <option value="client_instagram">{lang === 'ko' ? '인스타그램' : lang === 'fr' ? 'Instagram' : 'Instagram'}</option>
                <option value="memo">{lang === 'ko' ? '메모' : lang === 'fr' ? 'Mémo' : 'Memo'}</option>
              </select>
            </div>
            <input
              type="text"
              value={manualSearch}
              onChange={e => setManualSearch(e.target.value)}
              placeholder={lang === 'ko' ? '검색어를 입력하세요...' : lang === 'fr' ? 'Entrez votre recherche...' : 'Enter search term...'}
              className={styles.searchInput}
            />
            <button
              type="submit"
              title={TEXT.search[lang]}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                minWidth: '40px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </form>

          {/* 상태 필터 */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'nowrap', overflowX: 'auto', padding: '3px 0' }}>
            <button
              onClick={() => setManualStatus('all')}
              style={{
                background: manualStatus === 'all' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: manualStatus === 'all' ? '#333' : '#111',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '400',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (manualStatus !== 'all') {
                  e.target.style.background = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (manualStatus !== 'all') {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {lang === 'ko' ? '전체' : lang === 'fr' ? 'Tous' : 'All'}
            </button>
            <button
              onClick={() => setManualStatus('deposit_pending')}
              style={{
                background: manualStatus === 'deposit_pending' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: manualStatus === 'deposit_pending' ? '#333' : '#111',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '400',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (manualStatus !== 'deposit_pending') {
                  e.target.style.background = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (manualStatus !== 'deposit_pending') {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {TEXT.depositPending[lang]}
            </button>
            <button
              onClick={() => setManualStatus('deposit_received')}
              style={{
                background: manualStatus === 'deposit_received' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: manualStatus === 'deposit_received' ? '#333' : '#111',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '400',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (manualStatus !== 'deposit_received') {
                  e.target.style.background = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (manualStatus !== 'deposit_received') {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {TEXT.depositReceived[lang]}
            </button>
            <button
              onClick={() => setManualStatus('no_deposit')}
              style={{
                background: manualStatus === 'no_deposit' ? 'rgba(0,0,0,0.1)' : 'transparent',
                color: manualStatus === 'no_deposit' ? '#333' : '#111',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '400',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (manualStatus !== 'no_deposit') {
                  e.target.style.background = '#f0f0f0';
                }
              }}
              onMouseLeave={(e) => {
                if (manualStatus !== 'no_deposit') {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {lang === 'ko' ? '선금없음' : lang === 'fr' ? 'Pas d\'acompte' : 'No Deposit'}
            </button>
          </div>

          {/* 뷰 전환 버튼 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px', gap: '8px' }}>
            <button
              onClick={() => setViewMode('card')}
              style={{
                padding: '6px 12px',
                background: viewMode === 'card' ? '#333' : 'transparent',
                color: viewMode === 'card' ? '#fff' : '#333',
                border: '1px solid #333',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{
                padding: '6px 12px',
                background: viewMode === 'table' ? '#333' : 'transparent',
                color: viewMode === 'table' ? '#fff' : '#333',
                border: '1px solid #333',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          {/* 카드 뷰 */}
          {
            viewMode === 'card' && (
              <div className={styles.cardList}>
                {loadingManualReservations ? (
                  <div className={styles.emptyState}>{TEXT.loading[lang]}</div>
                ) : getSortedManualReservations().length === 0 ? (
                  <div className={styles.emptyState}>
                    {manualSearch.trim() || manualStatus !== 'all'
                      ? (lang === 'ko' ? '검색 결과가 없습니다.' : lang === 'fr' ? 'Aucun résultat.' : 'No results.')
                      : TEXT.noData[lang]}
                  </div>
                ) : (
                  getSortedManualReservations().map(r => {
                    // 예약 날짜가 오늘 이전인지 확인
                    const eventDate = r.relatedEvent?.start_time ? new Date(r.relatedEvent.start_time) : null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isPast = eventDate && eventDate < today;

                    return (
                      <div
                        key={r.id}
                        className={`${styles.reservationCard} ${isPast ? styles.reservationCardPast : ''}`}
                        onClick={() => openManualReservationModal(r)}
                      >
                        {/* 타투 이미지 썸네일 */}
                        {(() => {
                          const imageUrl = getReservationPrimaryImageUrl(r);

                          if (!imageUrl) {
                            return (
                              <div className={styles.cardThumbnailPlaceholder}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21,15 16,10 5,21" />
                                </svg>
                              </div>
                            );
                          }

                          return (
                            <img
                              src={imageUrl}
                              alt="Design"
                              className={styles.cardThumbnail}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          );
                        })()}
                        <div className={styles.cardLeft}>
                          <div className={styles.cardHeader}>
                            <span className={styles.cardClientName}>
                              {r.client_instagram || r.client_name || '-'}
                            </span>
                            {r.artist_name && (
                              <>
                                <span className={styles.cardArrow}>→</span>
                                <span className={styles.cardArtistName}>{r.artist_name}</span>
                              </>
                            )}
                          </div>
                          <div className={styles.cardMeta}>
                            {r.relatedEvent?.start_time && (
                              <span className={styles.cardMetaItem}>
                                {(() => {
                                  try {
                                    // 개인 캘린더 예약의 경우 해당 타임존으로 변환
                                    const eventTz = r.relatedEvent?.calendar_type === 'personal' && r.relatedEvent?.event_timezone
                                      ? r.relatedEvent.event_timezone
                                      : 'Europe/Paris';
                                    const startDT = DateTime.fromISO(r.relatedEvent.start_time, { zone: 'utc' }).setZone(eventTz);
                                    const endDT = r.relatedEvent.end_time
                                      ? DateTime.fromISO(r.relatedEvent.end_time, { zone: 'utc' }).setZone(eventTz)
                                      : null;
                                    const dateStr = startDT.toFormat('dd/MM');
                                    const startTime = startDT.toFormat('HH:mm');
                                    const endTime = endDT ? endDT.toFormat('HH:mm') : null;
                                    const timeStr = endTime ? `${dateStr} ${startTime}-${endTime}` : `${dateStr} ${startTime}`;
                                    // 개인 캘린더 예약의 경우 타임존 표시
                                    const timezone = r.relatedEvent?.calendar_type === 'personal' && r.relatedEvent?.event_timezone
                                      ? ` (${r.relatedEvent.event_timezone.split('/').pop()})`
                                      : '';
                                    return timeStr + timezone;
                                  } catch { return '-'; }
                                })()}
                              </span>
                            )}
                            <span className={styles.cardTypeBadge}>
                              {getReservationTypeLabel(r.reservation_type)}
                            </span>
                            {r.deposit_received && (
                              <span className={styles.cardStatus + ' ' + styles.cardStatusDeposit}>
                                ✓{r.deposit_amount > 0 ? ` ${r.deposit_amount}${r.currency && r.currency !== 'EUR' ? r.currency : '€'}` : ''}
                              </span>
                            )}
                            {r.deposit_amount > 0 && !r.deposit_received && (
                              <span className={styles.cardStatus + ' ' + styles.cardStatusPending}>{r.deposit_amount}{r.currency && r.currency !== 'EUR' ? r.currency : '€'}</span>
                            )}
                            {r.final_payment_received && (
                              <span className={styles.cardStatus + ' ' + styles.cardStatusFinalPayment}>
                                ✓{r.final_payment_amount > 0 ? ` ${r.final_payment_amount}${r.currency && r.currency !== 'EUR' ? r.currency : '€'}` : ''}
                                {r.final_payment_platform ? ` ${r.final_payment_platform.toUpperCase()}` : ''}
                                {r.final_payment_date && (
                                  <span style={{ fontSize: '0.85em', opacity: 0.7, marginLeft: '2px' }}>
                                    {(() => { try { return DateTime.fromISO(r.final_payment_date, { zone: 'utc' }).setZone('Europe/Paris').toFormat('dd/MM'); } catch { return ''; } })()}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          {/* GUEST SPOT 배지 - 개인 캘린더에서 추가된 예약 (별도 줄) */}
                          {r.relatedEvent?.calendar_type === 'personal' && (
                            <div style={{ marginTop: '4px' }}>
                              <span style={{
                                backgroundColor: '#40E0D0',
                                color: '#fff',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap'
                              }}>
                                GUEST SPOT{r.relatedEvent?.location_name ? ` : ${r.relatedEvent.location_name}` : ''}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className={styles.cardRight}>
                          <span className={styles.cardPrice}>
                            {r.min_price && r.max_price
                              ? `${r.min_price}-${r.max_price}${r.currency && r.currency !== 'EUR' ? ` ${r.currency}` : '€'}`
                              : r.min_price ? `${r.min_price}${r.currency && r.currency !== 'EUR' ? ` ${r.currency}` : '€'}`
                                : r.max_price ? `${r.max_price}${r.currency && r.currency !== 'EUR' ? ` ${r.currency}` : '€'}`
                                  : '-'}
                          </span>
                          {/* 유로 환산 표시 */}
                          {r.currency && r.currency !== 'EUR' && (r.min_price || r.max_price) && (
                            <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>
                              ≈ {formatCurrency(r.accounting?.expected_eur, 'EUR')}
                            </span>
                          )}
                        </div>
                        {r.memo && (
                          <div className={styles.cardMemo}>
                            {r.memo.length > 50 ? r.memo.substring(0, 50) + '...' : r.memo}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )
          }

          {/* 테이블 뷰 */}
          {
            viewMode === 'table' && (
              <div className={tableStyles.tableContainer}>
                <table className={tableStyles.table}>
                  <thead className={tableStyles.tableHeader}>
                    <tr>
                      <th
                        onClick={() => handleSort('event_start_time')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={lang === 'ko' ? '언제' : lang === 'fr' ? 'Quand' : 'When'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                          {sortField === 'event_start_time' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th style={{
                        width: '50px',
                        textAlign: 'center'
                      }}
                        title={TEXT.image[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21,15 16,10 5,21" />
                          </svg>
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('subsidy')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                      >
                        {TEXT.subsidy[lang]} {sortField === 'subsidy' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('reservation_type')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={TEXT.type[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                            <line x1="7" y1="7" x2="7.01" y2="7" />
                          </svg>
                          {sortField === 'reservation_type' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('client_instagram')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={TEXT.instagram[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#333' }}>
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z" />
                          </svg>
                          {sortField === 'client_instagram' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('client_name')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={TEXT.customerName[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          {sortField === 'client_name' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('price')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={TEXT.price[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>€</span>
                          {sortField === 'price' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('memo')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                        title={TEXT.memo[lang]}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10,9 9,9 8,9"></polyline>
                          </svg>
                          {sortField === 'memo' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('deposit_received_date')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                      >
                        {TEXT.depositReceivedDate[lang]} {sortField === 'deposit_received_date' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('deposit_platform')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                      >
                        {TEXT.depositPlatform[lang]} {sortField === 'deposit_platform' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                      </th>
                      <th
                        onClick={() => handleSort('created_at')}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                      >
                        {TEXT.creationDate[lang]} {sortField === 'created_at' ? (sortOrder === 'desc' ? '▼' : '▲') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={tableStyles.tableBody}>
                    {loadingManualReservations ? (
                      <tr><td colSpan={11} className={loadingStyles.loadingCell}>로딩 중...</td></tr>
                    ) : getSortedManualReservations().length === 0 ? (
                      <tr><td colSpan={11} className={loadingStyles.emptyCell}>
                        {manualSearch.trim() || manualStatus !== 'all'
                          ? (lang === 'ko' ? '검색 결과가 없습니다.' : lang === 'fr' ? 'Aucun résultat trouvé.' : 'No results found.')
                          : (lang === 'ko' ? '수동예약 정보가 없습니다.' : lang === 'fr' ? 'Aucune réservation manuelle.' : 'No manual reservations.')
                        }
                      </td></tr>
                    ) : (
                      getSortedManualReservations().map(r => (
                        <tr key={r.id} onClick={() => openManualReservationModal(r)} className={tableStyles.tableRow} style={{ cursor: 'pointer' }}>
                          <td>
                            {r.relatedEvent ? (
                              r.relatedEvent.start_time ? (
                                <span style={{
                                  color: '#333333',
                                  fontWeight: (() => {
                                    try {
                                      const eventDate = utcISOToParisDate(r.relatedEvent.start_time);
                                      const today = new Date();
                                      // 시간을 제외하고 날짜만 비교
                                      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                      return eventDateOnly < todayOnly ? '300' : '500';
                                    } catch (error) {
                                      return '500';
                                    }
                                  })(),
                                  whiteSpace: 'pre',
                                  lineHeight: '1.2',
                                  animation: (() => {
                                    try {
                                      const eventDate = utcISOToParisDate(r.relatedEvent.start_time);
                                      const today = new Date();
                                      // 시간을 제외하고 날짜만 비교
                                      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                                      // 오늘 날짜인지 확인
                                      const isToday = eventDateOnly.getTime() === todayOnly.getTime();

                                      // 가장 가까운 미래 날짜인지 확인
                                      const closestFutureDate = getClosestFutureDate();
                                      const isClosestFuture = closestFutureDate && eventDateOnly.getTime() === closestFutureDate.getTime();

                                      // 오늘이거나 가장 가까운 미래 날짜인 경우 애니메이션 적용
                                      return (isToday || isClosestFuture) ? 'fontWeightPulse 2s ease-in-out infinite' : 'none';
                                    } catch (error) {
                                      return 'none';
                                    }
                                  })()
                                }}>
                                  {(() => {
                                    try {
                                      // 개인 캘린더 예약의 경우 해당 타임존으로 변환
                                      const eventTz = r.relatedEvent?.calendar_type === 'personal' && r.relatedEvent?.event_timezone
                                        ? r.relatedEvent.event_timezone
                                        : 'Europe/Paris';
                                      const dateDT = DateTime.fromISO(r.relatedEvent.start_time, { zone: 'utc' }).setZone(eventTz);
                                      const day = dateDT.toFormat('dd');
                                      const month = dateDT.toFormat('MM');
                                      const year = dateDT.toFormat('yy');
                                      const hour = dateDT.toFormat('HH');
                                      const minute = dateDT.toFormat('mm');

                                      // 요일 배열 (다국어 지원)
                                      const weekdays = {
                                        ko: ['일', '월', '화', '수', '목', '금', '토'],
                                        en: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
                                        fr: ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM']
                                      };
                                      const weekday = weekdays[lang] || weekdays.en;
                                      const dayOfWeek = weekday[dateDT.weekday % 7]; // Luxon weekday: 1=Mon, 7=Sun

                                      // 모든 언어를 일/월/년 형식으로 통일
                                      const dateFormat = `${day}/${month}/${year}`;
                                      // 개인 캘린더 예약의 경우 타임존 표시
                                      const timezone = r.relatedEvent?.calendar_type === 'personal' && r.relatedEvent?.event_timezone
                                        ? `\n(${r.relatedEvent.event_timezone.split('/').pop()})`
                                        : '';
                                      return `${dateFormat}\n${dayOfWeek} ${hour}:${minute}${timezone}`;
                                    } catch (error) {
                                      console.error('예약일 포맷 에러:', error);
                                      return '-';
                                    }
                                  })()}
                                </span>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>날짜 없음</span>
                              )
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {lang === 'ko' ? '일정 확인 중...' : lang === 'fr' ? 'Vérification...' : 'Checking...'}
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: '2px' }}>
                            {(() => {
                              const imageUrl = getReservationPrimaryImageUrl(r);

                              if (!imageUrl) {
                                return <span style={{ color: '#ccc', fontSize: '12px' }}>-</span>;
                              }

                              return (
                                <img
                                  src={imageUrl}
                                  alt={TEXT.design[lang]}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s ease',
                                    animation: (() => {
                                      try {
                                        const eventDate = utcISOToParisDate(r.relatedEvent?.start_time);
                                        const today = new Date();
                                        const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                        const isToday = eventDateOnly.getTime() === todayOnly.getTime();
                                        const closestFutureDate = getClosestFutureDate();
                                        const isClosestFuture = closestFutureDate && eventDateOnly.getTime() === closestFutureDate.getTime();
                                        return (isToday || isClosestFuture) ? 'imagePulse 2s ease-in-out infinite' : 'none';
                                      } catch (error) {
                                        return 'none';
                                      }
                                    })()
                                  }}
                                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(imageUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              );
                            })()}
                          </td>
                          <td>
                            {r.deposit_amount ? (
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                fontWeight: '300',
                                color: '#000000'
                              }}>
                                <span>{r.deposit_amount}{r.currency && r.currency !== 'EUR' ? ` ${r.currency}` : ''}</span>
                                {r.deposit_received && (
                                  <span style={{
                                    fontSize: '14px',
                                    color: '#000000',
                                    fontWeight: 'normal'
                                  }}>
                                    ✓
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span style={{
                                fontWeight: '300',
                                color: '#000000'
                              }}>
                                -
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '300',
                              textTransform: 'uppercase',
                              color: '#333333'
                            }}>
                              {getReservationTypeLabel(r.reservation_type)}
                            </span>
                            {/* GUEST SPOT 배지 - 개인 캘린더에서 추가된 예약 (별도 줄) */}
                            {r.relatedEvent?.calendar_type === 'personal' && (
                              <div style={{ marginTop: '2px' }}>
                                <span style={{
                                  backgroundColor: '#40E0D0',
                                  color: '#fff',
                                  padding: '1px 4px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  GUEST{r.relatedEvent?.location_name ? ` : ${r.relatedEvent.location_name}` : ''}
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            <span style={{
                              fontWeight: (() => {
                                try {
                                  const eventDate = utcISOToParisDate(r.relatedEvent?.start_time);
                                  const today = new Date();
                                  const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                  return eventDateOnly < todayOnly ? '300' : '500';
                                } catch (error) { return '500'; }
                              })()
                            }}>
                              {r.client_instagram || '-'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontWeight: (() => {
                                try {
                                  const eventDate = utcISOToParisDate(r.relatedEvent?.start_time);
                                  const today = new Date();
                                  const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                  return eventDateOnly < todayOnly ? '300' : '500';
                                } catch (error) { return '500'; }
                              })()
                            }}>
                              {r.client_name || '-'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontWeight: (() => {
                                try {
                                  const eventDate = utcISOToParisDate(r.relatedEvent?.start_time);
                                  const today = new Date();
                                  const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                                  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                  return eventDateOnly < todayOnly ? '300' : '500';
                                } catch (error) { return '500'; }
                              })()
                            }}>
                              {r.min_price && r.max_price ?
                                `${r.min_price} ~ ${r.max_price}` :
                                r.min_price || r.max_price || '-'
                              }
                              {r.currency && r.currency !== 'EUR' && ` ${r.currency}`}
                            </span>
                            {/* 유로 환산 표시 */}
                            {r.currency && r.currency !== 'EUR' && (r.min_price || r.max_price) && (
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                ≈ {formatCurrency(r.accounting?.expected_eur, 'EUR')}
                              </div>
                            )}
                          </td>
                          <td>{r.memo || '-'}</td>
                          <td>
                            <span style={{
                              fontWeight: '300'
                            }}>
                              {r.deposit_received_date ?
                                formatUTCToLocalDateSlash(r.deposit_received_date, getLocaleByLang(lang)) :
                                '-'
                              }
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontWeight: '300'
                            }}>
                              {r.deposit_platform ?
                                (() => {
                                  switch (r.deposit_platform) {
                                    case 'paypal': return TEXT.paypal[lang];
                                    case 'lydia': return TEXT.lydia[lang];
                                    case 'cash': return TEXT.cash[lang];
                                    case 'bank_transfer': return TEXT.bankTransfer[lang];
                                    case 'other': return TEXT.other[lang];
                                    default: return r.deposit_platform;
                                  }
                                })() :
                                '-'
                              }
                            </span>
                          </td>
                          <td>
                            <span style={{
                              fontWeight: '300'
                            }}>
                              {r.created_at ?
                                formatUTCToLocalDateSlash(r.created_at, getLocaleByLang(lang)) :
                                '-'
                              }
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )
          }

          {/* 통계 섹션 */}
          <div style={{
            backgroundColor: 'transparent',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginTop: '10px',
            padding: '8px 12px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#495057',
                fontWeight: '400'
              }}>
                <span>{lang === 'ko' ? '총 개수:' : lang === 'fr' ? 'Total:' : 'Total Count:'}</span>
                <span style={{ textAlign: 'right' }}>{getSortedManualReservations().length}</span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#495057',
                fontWeight: '400'
              }}>
                <span>{lang === 'ko' ? '가격 합계:' : lang === 'fr' ? 'Total prix:' : 'Price Total:'}</span>
                <span style={{ textAlign: 'right' }}>{(() => {
                  const sortedReservations = getSortedManualReservations();
                  let totalMin = 0;
                  let totalMax = 0;

                  sortedReservations.forEach(r => {
                    const minPrice = r.accounting?.expected_eur ?? 0;
                    const maxPrice = r.accounting?.maximum_eur ?? minPrice;

                    if (r.accounting?.minimum_eur != null && r.accounting?.maximum_eur != null) {
                      totalMin += minPrice;
                      totalMax += maxPrice;
                    } else if (minPrice > 0) {
                      totalMin += minPrice;
                      totalMax += minPrice;
                    } else if (maxPrice > 0) {
                      totalMin += maxPrice;
                      totalMax += maxPrice;
                    }
                  });

                  if (totalMin === totalMax) {
                    return totalMin > 0 ? `${totalMin}` : '-';
                  } else {
                    return `${totalMin} ~ ${totalMax}`;
                  }
                })()}</span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#495057',
                fontWeight: '400'
              }}>
                <span>{lang === 'ko' ? '이미 받은 선금:' : lang === 'fr' ? 'Acompte reçu:' : 'Deposit Received:'}</span>
                <span style={{ textAlign: 'right' }}>{(() => {
                  const sortedReservations = getSortedManualReservations();
                  let totalDepositReceived = 0;

                  sortedReservations.forEach(r => {
                    if (r.deposit_amount && r.deposit_received) {
                      totalDepositReceived += r.accounting?.deposit_eur ?? 0;
                    }
                  });

                  return totalDepositReceived > 0 ? `${totalDepositReceived}` : '-';
                })()}</span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#495057',
                fontWeight: '400'
              }}>
                <span>{lang === 'ko' ? '오늘 이후 남은 금액 (받은선금 계산 적용):' : lang === 'fr' ? 'Montant restant après aujourd\'hui (acompte déduit):' : 'Remaining Amount After Today (Deposit Applied):'}</span>
                <span style={{ textAlign: 'right' }}>{(() => {
                  const sortedReservations = getSortedManualReservations();
                  let totalMin = 0;
                  let totalMax = 0;
                  let totalDepositReceived = 0;
                  const today = new Date();
                  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                  sortedReservations.forEach(r => {
                    let reservationDate = null;
                    try {
                      if (r.relatedEvent?.start_time) {
                        const eventDate = utcISOToParisDate(r.relatedEvent.start_time);
                        reservationDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                      } else if (r.created_at) {
                        const createdDate = new Date(r.created_at);
                        reservationDate = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
                      }
                    } catch (error) {
                      console.error('날짜 파싱 에러:', error);
                      return;
                    }

                    if (reservationDate && reservationDate > todayOnly) {
                      const minPrice = r.accounting?.expected_eur ?? 0;
                      const maxPrice = r.accounting?.maximum_eur ?? minPrice;

                      if (r.accounting?.minimum_eur != null && r.accounting?.maximum_eur != null) {
                        totalMin += minPrice;
                        totalMax += maxPrice;
                      } else if (minPrice > 0) {
                        totalMin += minPrice;
                        totalMax += minPrice;
                      } else if (maxPrice > 0) {
                        totalMin += maxPrice;
                        totalMax += maxPrice;
                      }

                      if (r.deposit_amount && r.deposit_received) {
                        totalDepositReceived += r.accounting?.deposit_eur ?? 0;
                      }
                    }
                  });

                  const remainingMin = totalMin - totalDepositReceived;
                  const remainingMax = totalMax - totalDepositReceived;

                  if (remainingMin <= 0 && remainingMax <= 0) {
                    return '-';
                  } else if (remainingMin === remainingMax) {
                    return remainingMin > 0 ? `${remainingMin}` : '-';
                  } else {
                    return `${remainingMin} ~ ${remainingMax}`;
                  }
                })()}</span>
              </div>
            </div>
          </div>

          {/* 버튼 영역 - 예약 탭 전용 */}
          <div style={{
            marginTop: '20px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
          }}>
            {/* 회계용 CSV 버튼 */}
            <button
              onClick={openAccountingCsvPreview}
              style={{
                padding: '4px 8px',
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                border: '1px solid #2e7d32',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '400',
                cursor: getSortedManualReservations().length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '100px',
                opacity: getSortedManualReservations().length === 0 ? 0.4 : 1,
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                if (getSortedManualReservations().length > 0) {
                  e.target.style.backgroundColor = '#1b5e20';
                }
              }}
              onMouseLeave={(e) => {
                if (getSortedManualReservations().length > 0) {
                  e.target.style.backgroundColor = '#2e7d32';
                }
              }}
              disabled={getSortedManualReservations().length === 0}
            >
              {lang === 'ko' ? '회계용 CSV' : lang === 'fr' ? 'CSV Comptable' : 'Accounting CSV'}
            </button>
            {/* 기존 백업 버튼 */}
            <button
              onClick={downloadCSV}
              style={{
                padding: '4px 8px',
                backgroundColor: '#fffdf8',
                color: '#333333',
                border: '1px solid #333333',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '300',
                cursor: getSortedManualReservations().length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '100px',
                opacity: getSortedManualReservations().length === 0 ? 0.4 : 1,
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                if (getSortedManualReservations().length > 0) {
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (getSortedManualReservations().length > 0) {
                  e.target.style.backgroundColor = '#ffffff';
                }
              }}
              disabled={getSortedManualReservations().length === 0}
            >
              {TEXT.backupAsTable[lang]}
            </button>
          </div>
        </>
      )}

      {/* 수동예약정보 상세 모달 */}
      {
        manualReservationModalOpen && selectedManualReservation && (
          <div
            key={`modal-${selectedManualReservation.id}-${selectedManualReservation.deposit_received}-${selectedManualReservation.deposit_received_date}`}
            className={modalStyles.modalOverlay}
            onClick={closeManualReservationModal}
            style={{
              overflow: 'hidden',
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'none',
              overscrollBehavior: 'none'
            }}
          >
            <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={modalStyles.modalHeader}>
                <h2>{TEXT.manualReservationDetails[lang]}</h2>
                <button className={modalStyles.closeButton} onClick={closeManualReservationModal}>×</button>
              </div>
              <div
                className={modalStyles.modalContent}
                style={{
                  WebkitOverflowScrolling: 'touch', // iOS 터치 스크롤 활성화
                  touchAction: 'pan-y', // PWA에서 세로 스크롤만 허용
                  paddingBottom: '60px', // 하단 패딩 증가 (버튼 영역 높이 + 여유)
                  marginBottom: '0', // 하단 마진 제거
                  overflowY: 'auto', // 세로 스크롤 활성화
                  maxHeight: 'calc(100vh - 180px)' // 버튼 영역 고려하여 높이 조정
                }}
              >
                <div className={modalContentStyles.modalInfoSection}>
                  {/* 타입 - 라벨 없이 박스로 표시 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      {isEditingManualReservation ? (
                        <ReservationTypeSelector
                          value={editManualReservationData.reservation_type || ''}
                          onChange={(value) => updateEditManualReservationField('reservation_type', value)}
                        />
                      ) : (
                        <div style={{
                          padding: '4px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa',
                          color: '#333',
                          fontSize: '12px',
                          fontWeight: '500',
                          textAlign: 'center',
                          textTransform: 'uppercase'
                        }}>
                          {getReservationTypeLabel(selectedManualReservation.reservation_type)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 인스타그램 + 사용자 이름 (한 줄) */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    {/* 인스타그램 */}
                    <div className={modalContentStyles.modalInfoItem} style={{ flex: 1, minWidth: '45%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#E4405F', flexShrink: 0 }}>
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z" />
                        </svg>
                        {isEditingManualReservation ? (
                          <input
                            type="text"
                            value={editManualReservationData.client_instagram || ''}
                            onChange={(e) => updateEditManualReservationField('client_instagram', e.target.value)}
                            className="manual-reservation-input"
                            style={{
                              flex: 1,
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff !important',
                              color: '#000000 !important',
                              background: '#ffffff !important',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          <div className={modalContentStyles.modalInfoValue}>
                            {selectedManualReservation.client_instagram && (
                              <a
                                href={`https://instagram.com/${selectedManualReservation.client_instagram.replace('@', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#000000',
                                  textDecoration: 'none',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                {selectedManualReservation.client_instagram}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* 사용자 이름 */}
                    <div className={modalContentStyles.modalInfoItem} style={{ flex: 1, minWidth: '45%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333', flexShrink: 0 }}>
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        {isEditingManualReservation ? (
                          <input
                            type="text"
                            value={editManualReservationData.client_name || ''}
                            onChange={(e) => updateEditManualReservationField('client_name', e.target.value)}
                            className="manual-reservation-input"
                            style={{
                              flex: 1,
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff !important',
                              color: '#000000 !important',
                              background: '#ffffff !important',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          <div className={modalContentStyles.modalInfoValue}>{selectedManualReservation.client_name}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        {isEditingManualReservation ? (
                          <input
                            type="tel"
                            value={editManualReservationData.client_phone || ''}
                            onChange={(e) => updateEditManualReservationField('client_phone', e.target.value)}
                            className="manual-reservation-input"
                            style={{
                              flex: 1,
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff !important',
                              color: '#000000 !important',
                              background: '#ffffff !important',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          <div className={modalContentStyles.modalInfoValue}>{selectedManualReservation.client_phone}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 이메일 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                        {isEditingManualReservation ? (
                          <input
                            type="email"
                            value={editManualReservationData.client_email || ''}
                            onChange={(e) => updateEditManualReservationField('client_email', e.target.value)}
                            className="manual-reservation-input"
                            style={{
                              flex: 1,
                              padding: '4px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff !important',
                              color: '#000000 !important',
                              background: '#ffffff !important',
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          <div className={modalContentStyles.modalInfoValue}>{selectedManualReservation.client_email}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* 선금 - 두 줄 레이아웃 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      {isEditingManualReservation ? (
                        /* 수정 모드: 두 줄 레이아웃 (라벨 / 입력폼) */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {/* 첫 줄: 라벨 */}
                          <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', textTransform: 'uppercase', fontSize: '12px' }}>{TEXT.deposit[lang]}</div>
                          {/* 둘째 줄: 입력폼 */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <input
                              type="number"
                              step="10"
                              placeholder={TEXT.depositAmount[lang]}
                              value={editManualReservationData.deposit_amount || ''}
                              onChange={(e) => updateEditManualReservationField('deposit_amount', e.target.value)}
                              style={{ width: '60px', padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                            />
                            {/* 수령 체크박스 */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                              <div
                                style={{ width: '14px', height: '14px', border: '1px solid #000', backgroundColor: '#fffdf8', borderRadius: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => {
                                  const newValue = !editManualReservationData.deposit_received;
                                  updateEditManualReservationField('deposit_received', newValue);
                                }}
                              >
                                {Boolean(editManualReservationData.deposit_received) && (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20,6 9,17 4,12"></polyline>
                                  </svg>
                                )}
                              </div>
                              <span>{TEXT.received[lang]}</span>
                            </label>
                            {/* 날짜와 방법 - 체크 시에만 */}
                            {Boolean(editManualReservationData.deposit_received) && (
                              <>
                                <input
                                  type="date"
                                  value={dateInputValue}
                                  onChange={(e) => {
                                    const dateValue = e.target.value;
                                    setDateInputValue(dateValue);
                                    updateEditManualReservationField(
                                      'deposit_received_date',
                                      buildManualReservationDepositReceivedDate(dateValue)
                                    );
                                  }}
                                  style={{ padding: '4px 6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '120px', flex: '1 1 auto' }}
                                />
                                <select
                                  value={editManualReservationData.deposit_platform || ''}
                                  onChange={(e) => updateEditManualReservationField('deposit_platform', e.target.value)}
                                  style={{ padding: '4px 6px', fontSize: '12px', textTransform: 'uppercase', border: '1px solid #ddd', borderRadius: '4px', minWidth: '80px', flex: '1 1 auto' }}
                                >
                                  <option value="">{lang === 'ko' ? '수단' : lang === 'fr' ? 'Moyen' : 'Method'}</option>
                                  <option value="paypal">{TEXT.paypal[lang]}</option>
                                  <option value="lydia">{TEXT.lydia[lang]}</option>
                                  <option value="cash">{TEXT.cash[lang]}</option>
                                  <option value="bank_transfer">{TEXT.bankTransfer[lang]}</option>
                                  <option value="card">{lang === 'ko' ? '카드' : lang === 'fr' ? 'Carte' : 'Card'}</option>
                                  <option value="other">{TEXT.other[lang]}</option>
                                </select>
                              </>
                            )}
                          </div>
                          {/* 선금 유로 환산 */}
                          {editManualReservationData.currency && editManualReservationData.currency !== 'EUR' && editManualReservationData.deposit_amount && (
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              ≈ {formatCurrency(reservationMoneyPreview(parseFloat(editManualReservationData.deposit_amount), editManualReservationData, exchangeRates), 'EUR')}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* 읽기 모드: 한 줄 표시 */
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', textTransform: 'uppercase', fontSize: '12px' }}>{TEXT.deposit[lang]}</div>
                          <span>
                            {selectedManualReservation.deposit_amount || ''}
                            {selectedManualReservation.currency && selectedManualReservation.currency !== 'EUR' && ` ${selectedManualReservation.currency}`}
                          </span>
                          {/* 선금 유로 환산 */}
                          {selectedManualReservation.currency && selectedManualReservation.currency !== 'EUR' && selectedManualReservation.deposit_amount && (
                            <span style={{ fontSize: '11px', color: '#666' }}>
                              (≈ {formatCurrency(reservationMoneyPreview(parseFloat(selectedManualReservation.deposit_amount), selectedManualReservation, exchangeRates), 'EUR')})
                            </span>
                          )}
                          {selectedManualReservation.deposit_received ? (
                            <>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#28a745' }}>
                                <polyline points="20,6 9,17 4,12"></polyline>
                              </svg>
                              {selectedManualReservation.deposit_platform && (
                                <span style={{ fontSize: '12px', color: '#666', fontWeight: '300' }}>
                                  {selectedManualReservation.deposit_platform.toUpperCase()}
                                </span>
                              )}
                              {selectedManualReservation.deposit_received_date && (
                                <span style={{ fontSize: '12px', color: '#666', fontWeight: '300' }}>
                                  {formatUTCToLocalDateSlash(selectedManualReservation.deposit_received_date, getLocaleByLang(lang))}
                                </span>
                              )}
                            </>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ffc107' }}>
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12,6 12,12 16,14"></polyline>
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 가격 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', textTransform: 'uppercase', fontSize: '12px' }}>{TEXT.price[lang]}</div>
                        {isEditingManualReservation ? (
                          <>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flex: 1, width: '100%' }}>
                              <input
                                type="number"
                                step="10"
                                placeholder={TEXT.minPrice[lang]}
                                value={editManualReservationData.min_price || ''}
                                onChange={(e) => updateEditManualReservationField('min_price', e.target.value)}
                                className="manual-reservation-input"
                                style={{
                                  flex: '1 1 45%',
                                  padding: '4px 6px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  minWidth: '60px',
                                  maxWidth: '120px',
                                  backgroundColor: '#ffffff !important',
                                  color: '#000000 !important',
                                  background: '#ffffff !important',
                                  fontSize: '14px',
                                  boxSizing: 'border-box'
                                }}
                              />
                              <span style={{ fontSize: '12px', color: '#666', flexShrink: 0, margin: '0 2px' }}>~</span>
                              <input
                                type="number"
                                step="10"
                                placeholder={TEXT.maxPrice[lang]}
                                value={editManualReservationData.max_price || ''}
                                onChange={(e) => updateEditManualReservationField('max_price', e.target.value)}
                                className="manual-reservation-input"
                                style={{
                                  flex: '1 1 45%',
                                  padding: '4px 6px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  minWidth: '60px',
                                  maxWidth: '120px',
                                  backgroundColor: '#ffffff !important',
                                  color: '#000000 !important',
                                  background: '#ffffff !important',
                                  fontSize: '14px',
                                  boxSizing: 'border-box'
                                }}
                              />
                              {/* 통화 선택 드롭다운 - 개인 캘린더인 경우에만 */}
                              {selectedManualReservation.relatedEvent?.calendar_type === 'personal' && (
                                <select
                                  value={editManualReservationData.currency || 'EUR'}
                                  onChange={(e) => updateEditManualReservationField('currency', e.target.value)}
                                  style={{
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    fontSize: '12px',
                                    backgroundColor: '#fffdf8',
                                    color: '#333'
                                  }}
                                >
                                  {CURRENCY_OPTIONS.map(c => (
                                    <option key={c.code} value={c.code}>{c.code}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            {/* 유로 환산 표시 - 다음 줄 */}
                            {editManualReservationData.currency && editManualReservationData.currency !== 'EUR' && (editManualReservationData.min_price || editManualReservationData.max_price) && (
                              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', marginLeft: '40px', width: '100%' }}>
                                ≈ {editManualReservationData.min_price && editManualReservationData.max_price
                                  ? `${formatCurrency(reservationMoneyPreview(parseFloat(editManualReservationData.min_price), editManualReservationData, exchangeRates), 'EUR')} ~ ${formatCurrency(reservationMoneyPreview(parseFloat(editManualReservationData.max_price), editManualReservationData, exchangeRates), 'EUR')}`
                                  : formatCurrency(reservationMoneyPreview(parseFloat(editManualReservationData.min_price || editManualReservationData.max_price), editManualReservationData, exchangeRates), 'EUR')}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={modalContentStyles.modalInfoValue}>
                            {selectedManualReservation.min_price && selectedManualReservation.max_price ?
                              `${selectedManualReservation.min_price} ~ ${selectedManualReservation.max_price}` :
                              selectedManualReservation.min_price || selectedManualReservation.max_price
                            }
                            {selectedManualReservation.currency && selectedManualReservation.currency !== 'EUR' && ` ${selectedManualReservation.currency}`}
                            {/* 유로 환산 */}
                            {selectedManualReservation.currency && selectedManualReservation.currency !== 'EUR' && (selectedManualReservation.min_price || selectedManualReservation.max_price) && (
                              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', width: '100%' }}>
                                ≈ {selectedManualReservation.min_price && selectedManualReservation.max_price
                                  ? `${formatCurrency(reservationMoneyPreview(parseFloat(selectedManualReservation.min_price), selectedManualReservation, exchangeRates), 'EUR')} ~ ${formatCurrency(reservationMoneyPreview(parseFloat(selectedManualReservation.max_price), selectedManualReservation, exchangeRates), 'EUR')}`
                                  : formatCurrency(reservationMoneyPreview(parseFloat(selectedManualReservation.min_price || selectedManualReservation.max_price), selectedManualReservation, exchangeRates), 'EUR')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 최종 결제 정보 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '4px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      {isEditingManualReservation ? (
                        /* 편집 모드: 두 줄 레이아웃 (라벨 / 입력폼) */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {/* 첫 줄: 라벨 + 잔금 표시 */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', textTransform: 'uppercase', fontSize: '12px' }}>{TEXT.finalPayment[lang]}</div>
                            {/* 잔금 자동 계산 표시 */}
                            {(() => {
                              const remaining = getManualReservationRemainingAmount(editManualReservationData);
                              return remaining > 0 ? (
                                <span style={{ fontSize: '11px', color: '#666' }}>
                                  ({TEXT.remainingAmount[lang]}: {remaining.toFixed(0)}€)
                                </span>
                              ) : null;
                            })()}
                          </div>
                          {/* 둘째 줄: 입력폼 */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <input
                              type="checkbox"
                              checked={editManualReservationData.final_payment_received || false}
                              onChange={(e) => {
                                const isReceived = e.target.checked;
                                setEditManualReservationData(prev => buildFinalPaymentToggleEditData(prev, isReceived));
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '12px', textTransform: 'uppercase' }}>{TEXT.received[lang]}</span>
                            {editManualReservationData.final_payment_received && (
                              <>
                                <input
                                  type="number"
                                  step="10"
                                  placeholder={lang === 'ko' ? '금액' : lang === 'fr' ? 'Montant' : 'Amount'}
                                  value={editManualReservationData.final_payment_amount || ''}
                                  onChange={(e) => updateEditManualReservationField('final_payment_amount', e.target.value)}
                                  style={{
                                    width: '80px',
                                    padding: '4px 6px',
                                    fontSize: '12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#fffdf8',
                                    color: '#000000'
                                  }}
                                />
                                <input
                                  type="date"
                                  value={editManualReservationData.final_payment_date || ''}
                                  onChange={(e) => updateEditManualReservationField('final_payment_date', e.target.value)}
                                  style={{ padding: '4px 6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '120px', flex: '1 1 auto' }}
                                />
                                <select
                                  value={editManualReservationData.final_payment_platform || ''}
                                  onChange={(e) => updateEditManualReservationField('final_payment_platform', e.target.value)}
                                  style={{ padding: '4px 6px', fontSize: '12px', textTransform: 'uppercase', border: '1px solid #ddd', borderRadius: '4px', minWidth: '80px', flex: '1 1 auto' }}
                                >
                                  <option value="">{lang === 'ko' ? '수단' : lang === 'fr' ? 'Moyen' : 'Method'}</option>
                                  <option value="paypal">{TEXT.paypal[lang]}</option>
                                  <option value="lydia">{TEXT.lydia[lang]}</option>
                                  <option value="cash">{TEXT.cash[lang]}</option>
                                  <option value="bank_transfer">{TEXT.bankTransfer[lang]}</option>
                                  <option value="card">{lang === 'ko' ? '카드' : lang === 'fr' ? 'Carte' : 'Card'}</option>
                                  <option value="other">{TEXT.other[lang]}</option>
                                </select>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* 읽기 모드: 최종 결제 정보 표시 */
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', textTransform: 'uppercase', fontSize: '12px' }}>{TEXT.finalPayment[lang]}</div>
                          {selectedManualReservation.final_payment_received ? (
                            <>
                              <span>{selectedManualReservation.final_payment_amount || ''}{selectedManualReservation.currency && selectedManualReservation.currency !== 'EUR' ? ` ${selectedManualReservation.currency}` : '€'}</span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#28a745' }}>
                                <polyline points="20,6 9,17 4,12"></polyline>
                              </svg>
                              {selectedManualReservation.final_payment_platform && (
                                <span style={{ fontSize: '12px', color: '#666', fontWeight: '300' }}>
                                  {selectedManualReservation.final_payment_platform.toUpperCase()}
                                </span>
                              )}
                              {selectedManualReservation.final_payment_date && (
                                <span style={{ fontSize: '12px', color: '#666', fontWeight: '300' }}>
                                  {formatUTCToLocalDateSlash(selectedManualReservation.final_payment_date, getLocaleByLang(lang))}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {/* 잔금 표시 */}
                              {(() => {
                                const remaining = getManualReservationRemainingAmount(selectedManualReservation);
                                return remaining > 0 ? (
                                  <span style={{ fontSize: '11px', color: '#666' }}>
                                    ({TEXT.remainingAmount[lang]}: {remaining.toFixed(0)}€)
                                  </span>
                                ) : null;
                              })()}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ffc107' }}>
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12,6 12,12 16,14"></polyline>
                              </svg>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 메모 정보 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '8px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '4px' }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14,2 14,8 20,8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10,9 9,9 8,9"></polyline>
                        </svg>
                        {isEditingManualReservation ? (
                          <textarea
                            value={editManualReservationData.memo || ''}
                            onChange={(e) => {
                              updateEditManualReservationField('memo', e.target.value);
                              resizeManualReservationTextarea(e.target);
                            }}
                            onFocus={(e) => {
                              resizeManualReservationTextarea(e.target);
                            }}
                            className="manual-reservation-input"
                            style={{
                              flex: 1,
                              padding: '4px 6px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '12px',
                              minHeight: '24px',
                              height: 'auto',
                              resize: 'none',
                              overflow: 'hidden',
                              backgroundColor: '#ffffff !important',
                              color: '#000000 !important',
                              background: '#ffffff !important'
                            }}
                            placeholder={TEXT.enterMemo[lang]}
                          />
                        ) : (
                          <div className={modalContentStyles.modalInfoValue} style={{ flex: 1 }}>
                            {selectedManualReservation.memo}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 생성일 */}
                  <div className={modalContentStyles.modalInfoRow} style={{ marginBottom: '8px' }}>
                    <div className={modalContentStyles.modalInfoItem} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className={modalContentStyles.modalInfoLabel} style={{ fontWeight: '400', fontSize: '12px' }}>{TEXT.creationDate[lang]}</div>
                        <div className={modalContentStyles.modalInfoValue} style={{ color: '#666', fontSize: '12px' }}>
                          {selectedManualReservation.created_at &&
                            formatUTCToLocalDate(selectedManualReservation.created_at, getLocaleByLang(lang))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 연결된 일정 정보 */}
                <div
                  style={{
                    marginTop: '7px',
                    padding: '5px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e9ecef';
                    e.target.style.borderColor = '#28a745';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.borderColor = '#e9ecef';
                  }}
                  onClick={() => {
                    if (selectedManualReservation.relatedEvent?.start_time) {
                      try {
                        // UTC 시간을 파리 시간대로 변환
                        const utcDate = new Date(selectedManualReservation.relatedEvent.start_time);
                        const parisDate = DateTime.fromJSDate(utcDate).setZone('Europe/Paris');

                        // 언어별 주 시작일 계산
                        let weekStart;
                        if (lang === 'ko') {
                          // 한국어: 일요일이 주의 시작
                          const dayOfWeek = parisDate.weekday % 7; // 0=일요일, 1=월요일, ...
                          weekStart = parisDate.minus({ days: dayOfWeek });
                        } else {
                          // 영어/불어: 월요일이 주의 시작
                          const dayOfWeek = parisDate.weekday % 7; // 0=일요일, 1=월요일, ...
                          const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 일요일이면 6일 전, 아니면 dayOfWeek-1일 전
                          weekStart = parisDate.minus({ days: daysToMonday });
                        }

                        const weekStartStr = weekStart.toFormat('yyyy-MM-dd');

                        // 달력 페이지로 이동하면서 주간뷰와 날짜 파라미터 전달
                        navigate(`/calendar?view=timeGridWeek&date=${weekStartStr}`);
                      } catch (error) {
                        console.error('날짜 계산 에러:', error);
                        // 에러 발생 시 기본 달력 페이지로 이동
                        navigate('/calendar');
                      }
                    }
                  }}
                >
                  <h4 style={{
                    margin: '0 0 4px 0',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#333'
                  }}>
                    {TEXT.connectedEvent[lang]}
                  </h4>
                  {selectedManualReservation.relatedEvent ? (
                    <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.1' }}>
                      <div style={{ marginBottom: '2px' }}>
                        <strong style={{ fontWeight: '400' }}>{TEXT.title[lang]}:</strong>
                        <span style={{ color: '#28a745', fontSize: '13px', fontWeight: '400' }}>
                          {selectedManualReservation.relatedEvent.title || TEXT.noTitle[lang]}
                        </span>
                      </div>
                      <div style={{ marginBottom: '2px' }}>
                        <strong style={{ fontWeight: '400' }}>{TEXT.start[lang]}:</strong> {
                          selectedManualReservation.relatedEvent.start_time ?
                            (() => {
                              try {
                                // 개인 캘린더 예약의 경우 해당 타임존으로 변환
                                const eventTz = selectedManualReservation.relatedEvent?.calendar_type === 'personal' && selectedManualReservation.relatedEvent?.event_timezone
                                  ? selectedManualReservation.relatedEvent.event_timezone
                                  : 'Europe/Paris';
                                const dateDT = DateTime.fromISO(selectedManualReservation.relatedEvent.start_time, { zone: 'utc' }).setZone(eventTz);

                                // 요일 배열 (다국어 지원)
                                const weekdays = {
                                  ko: ['일', '월', '화', '수', '목', '금', '토'],
                                  en: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
                                  fr: ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM']
                                };
                                const weekday = weekdays[lang] || weekdays.en;
                                const dayOfWeek = weekday[dateDT.weekday % 7];

                                // 날짜와 시간 포맷
                                const dateTimeStr = dateDT.toFormat('dd/MM/yyyy HH:mm');

                                // 개인 캘린더인 경우 타임존 이름 표시
                                const tzLabel = selectedManualReservation.relatedEvent?.calendar_type === 'personal' && selectedManualReservation.relatedEvent?.event_timezone
                                  ? ` (${selectedManualReservation.relatedEvent.event_timezone.split('/').pop()})`
                                  : '';

                                return `${dateTimeStr} ${dayOfWeek}${tzLabel}`;
                              } catch (error) {
                                console.error('시작시간 포맷 에러:', error);
                                return '-';
                              }
                            })() :
                            '-'
                        }
                      </div>
                      <div style={{ marginBottom: '2px' }}>
                        <strong style={{ fontWeight: '400' }}>{TEXT.end[lang]}:</strong> {
                          selectedManualReservation.relatedEvent.end_time ?
                            (() => {
                              try {
                                // 개인 캘린더 예약의 경우 해당 타임존으로 변환
                                const eventTz = selectedManualReservation.relatedEvent?.calendar_type === 'personal' && selectedManualReservation.relatedEvent?.event_timezone
                                  ? selectedManualReservation.relatedEvent.event_timezone
                                  : 'Europe/Paris';
                                const dateDT = DateTime.fromISO(selectedManualReservation.relatedEvent.end_time, { zone: 'utc' }).setZone(eventTz);

                                // 요일 배열 (다국어 지원)
                                const weekdays = {
                                  ko: ['일', '월', '화', '수', '목', '금', '토'],
                                  en: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
                                  fr: ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM']
                                };
                                const weekday = weekdays[lang] || weekdays.en;
                                const dayOfWeek = weekday[dateDT.weekday % 7];

                                // 날짜와 시간 포맷
                                const dateTimeStr = dateDT.toFormat('dd/MM/yyyy HH:mm');

                                // 개인 캘린더인 경우 타임존 이름 표시
                                const tzLabel = selectedManualReservation.relatedEvent?.calendar_type === 'personal' && selectedManualReservation.relatedEvent?.event_timezone
                                  ? ` (${selectedManualReservation.relatedEvent.event_timezone.split('/').pop()})`
                                  : '';

                                return `${dateTimeStr} ${dayOfWeek}${tzLabel}`;
                              } catch (error) {
                                console.error('종료시간 포맷 에러:', error);
                                return '-';
                              }
                            })() :
                            '-'
                        }
                      </div>

                      {selectedManualReservation.relatedEvent.is_all_day && (
                        <div style={{ marginBottom: '2px' }}>
                          <strong style={{ fontWeight: '400' }}>{TEXT.allDay[lang]}:</strong> <span style={{ color: '#28a745' }}>{TEXT.yes[lang]}</span>
                        </div>
                      )}
                    </div>
                  ) : selectedManualReservation.eventError ? (
                    <span style={{ color: '#dc3545', fontStyle: 'italic', fontSize: '13px' }}>
                      {selectedManualReservation.eventError}
                    </span>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic', fontSize: '13px' }}>
                      {TEXT.noConnectedEvent[lang]}
                    </span>
                  )}
                  {selectedManualReservation.relatedEvent?.start_time && (
                    <div style={{
                      marginTop: '8px',
                      fontSize: '11px',
                      color: '#28a745',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: '4px',
                      backgroundColor: 'rgba(40, 167, 69, 0.1)',
                      borderRadius: '4px',
                      border: '1px dashed #28a745'
                    }}>
                      {lang === 'ko' ? '클릭하여 달력에서 확인' :
                        lang === 'fr' ? 'Cliquez pour voir dans le calendrier' :
                          'Click to view in calendar'}
                    </div>
                  )}
                </div>


                {/* 디자인 이미지 */}
                <div style={{
                  marginTop: '7px',
                  marginBottom: '10px',
                  padding: '5px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#333' }}>
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21,15 16,10 5,21" />
                    </svg>
                  </div>

                  {/* 복수 이미지 갤러리 - EditEventModal과 동일한 방식 */}
                  <ManualReservationImageGallery
                    reservationId={selectedManualReservation.id}
                    images={editManualReservationData.images || selectedManualReservation.images || []}
                    legacyImage={selectedManualReservation.design_image}
                    isEditing={isEditingManualReservation}
                    disabled={false}
                    lang={lang}
                    onImagesChange={(newImages) => {
                      updateEditManualReservationField('images', newImages);
                    }}
                    onImageClick={(imageUrl) => {
                      window.open(imageUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
                    }}
                    onError={(error) => {
                      console.error('이미지 오류:', error);
                    }}
                  />
                </div>
              </div>
              <div className={modalActionStyles.modalActions} style={{
                width: '100%',
                paddingLeft: '5px',
                paddingRight: '5px',
                display: 'flex',
                gap: '4px'
              }}>
                <div
                  onClick={() => {
                    // 연결된 일정이 있는지 확인
                    const hasRelatedEvent = selectedManualReservation.relatedEvent;

                    if (hasRelatedEvent) {
                      // 연결된 일정이 있는 경우 삭제 선택 모달 열기
                      setDeleteChoiceModalOpen(true);
                    } else {
                      // 연결된 일정이 없는 경우 바로 삭제 확인
                      if (window.confirm(TEXT.reallyDelete[lang])) {
                        deleteManualReservationOnly();
                      }
                    }
                  }}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  title={TEXT.delete[lang]}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#dc3545' }}>
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </div>
                {/* 뒤로가기 버튼 - 수정 모드일 때만 표시 */}
                {isEditingManualReservation && (
                  <div
                    onClick={() => setIsEditingManualReservation(false)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    title={lang === 'ko' ? '취소' : lang === 'fr' ? 'Annuler' : 'Cancel'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#666' }}>
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12,19 5,12 12,5"></polyline>
                    </svg>
                  </div>
                )}
                <div
                  onClick={isEditingManualReservation ? handleManualReservationSave : toggleEditMode}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  title={isEditingManualReservation ? TEXT.editConfirm[lang] : TEXT.edit[lang]}
                >
                  {isEditingManualReservation ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#28a745' }}>
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#28a745' }}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* 삭제 선택 모달 */}
      {
        deleteChoiceModalOpen && (
          <div
            className={modalStyles.modalOverlay}
            onClick={() => setDeleteChoiceModalOpen(false)}
            style={{
              overflow: 'hidden',
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'none',
              overscrollBehavior: 'none'
            }}
          >
            <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <div className={modalStyles.modalHeader}>
                <h2>{TEXT.deleteChoice[lang]}</h2>
                <button className={modalStyles.closeButton} onClick={() => setDeleteChoiceModalOpen(false)}>×</button>
              </div>
              <div
                className={modalStyles.modalContent}
                style={{
                  WebkitOverflowScrolling: 'touch', // iOS 터치 스크롤 활성화
                  touchAction: 'pan-y', // PWA에서 세로 스크롤만 허용
                  paddingBottom: '60px', // 하단 패딩 추가 (버튼 영역 공간 확보)
                  marginBottom: '0', // 하단 마진 제거
                  overflowY: 'auto', // 세로 스크롤 활성화
                  maxHeight: 'calc(100vh - 180px)' // 최대 높이 설정 (헤더, 액션 버튼, 여백 고려)
                }}
              >
                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#333',
                  marginBottom: '20px'
                }}>
                  <p style={{ marginBottom: '10px' }}>
                    {TEXT.hasConnectedEvent[lang]}
                  </p>
                  <p style={{ marginBottom: '15px', fontWeight: '500' }}>
                    {TEXT.chooseDeleteMethod[lang]}
                  </p>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    backgroundColor: '#f8f9fa',
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '10px'
                  }}>
                    <strong>{TEXT.note[lang]}</strong> {TEXT.deleteReservationOnlyNote[lang]}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={async () => {
                      setDeleteChoiceModalOpen(false);
                      await deleteManualReservationWithEvent();
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #dc3545',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#c82333';
                      e.target.style.borderColor = '#c82333';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#dc3545';
                      e.target.style.borderColor = '#dc3545';
                    }}
                  >
                    <span className="button-text animate">
                      {TEXT.deleteReservationAndEvent[lang]}
                    </span>
                  </button>

                  <button
                    onClick={async () => {
                      setDeleteChoiceModalOpen(false);
                      await deleteManualReservationOnly();
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #ffc107',
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e0a800';
                      e.target.style.borderColor = '#e0a800';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ffc107';
                      e.target.style.borderColor = '#ffc107';
                    }}
                  >
                    <span className="button-text animate">
                      {TEXT.deleteReservationOnly[lang]}
                    </span>
                  </button>

                  <button
                    onClick={() => setDeleteChoiceModalOpen(false)}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #6c757d',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#5a6268';
                      e.target.style.borderColor = '#5a6268';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#6c757d';
                      e.target.style.borderColor = '#6c757d';
                    }}
                  >
                    <span className="button-text animate">
                      {TEXT.goBack[lang]}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* CSS 애니메이션 스타일 및 입력폼 배경색 강제 설정 */}
      <style>
        {`
          @keyframes slideText {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(-20px); }
          }
          
          @keyframes slideTextLong {
            0% { transform: translateX(0); }
            25% { transform: translateX(0); }
            75% { transform: translateX(-30px); }
            100% { transform: translateX(0); }
          }
          
          @keyframes fontWeightPulse {
            0%, 100% { font-weight: 300; }
            50% { font-weight: 500; }
          }
          
          @keyframes imagePulse {
            0%, 100% { 
              transform: scale(1);
            }
            50% { 
              transform: scale(1.1);
            }
          }
          
          .button-text {
            display: inline-block;
            white-space: nowrap;
            transition: transform 0.3s ease;
          }
          
          .button-text.animate {
            animation: slideTextLong 4s ease-in-out infinite;
          }
          
          /* 모달 내부 모든 입력폼 강제 흰색 배경 - 더 구체적인 선택자 사용 */
          [class*="modal"] input[type="text"],
          [class*="modal"] input[type="email"],
          [class*="modal"] input[type="tel"],
          [class*="modal"] input[type="number"],
          [class*="modal"] input[type="date"],
          [class*="modal"] input[type="file"],
          [class*="modal"] select,
          [class*="modal"] textarea,
          .modal input[type="text"],
          .modal input[type="email"],
          .modal input[type="tel"],
          .modal input[type="number"],
          .modal input[type="date"],
          .modal input[type="file"],
          .modal select,
          .modal textarea {
            background-color: #ffffff !important;
            background: #ffffff !important;
            color: #000000 !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
          }
          
          /* 모달 내부 모든 옵션 요소도 흰색 배경 */
          [class*="modal"] option,
          .modal option {
            background-color: #ffffff !important;
            background: #ffffff !important;
            color: #000000 !important;
          }
          
          /* 모든 입력 요소에 대한 강제 스타일 */
          input, select, textarea {
            background-color: #ffffff !important;
            background: #ffffff !important;
            color: #000000 !important;
          }
          
          /* 체크박스 스타일 강제 설정 */
          input[type="checkbox"] {
            background-color: #ffffff !important;
            border: 1px solid #ccc !important;
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 3px;
            position: relative;
            cursor: pointer;
          }

          input[type="checkbox"]:checked {
            background-color: #c00d0d !important;
            border-color: #c00d0d !important;
          }

          input[type="checkbox"]:checked::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            font-size: 12px;
            font-weight: bold;
          }
          
          /* 수동예약 입력폼 강제 스타일 */
          .manual-reservation-input,
          input.manual-reservation-input,
          select.manual-reservation-input,
          textarea.manual-reservation-input {
            background-color: transparent !important;
            background: transparent !important;
            color: #000000 !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            border: 1px solid #ddd !important;
          }
          
          /* 수동예약 입력폼의 모든 상태에 대한 강제 스타일 */
          .manual-reservation-input:focus,
          .manual-reservation-input:hover,
          .manual-reservation-input:active,
          .manual-reservation-input:disabled,
          input.manual-reservation-input:focus,
          input.manual-reservation-input:hover,
          input.manual-reservation-input:active,
          input.manual-reservation-input:disabled,
          select.manual-reservation-input:focus,
          select.manual-reservation-input:hover,
          select.manual-reservation-input:active,
          select.manual-reservation-input:disabled,
          textarea.manual-reservation-input:focus,
          textarea.manual-reservation-input:hover,
          textarea.manual-reservation-input:active,
          textarea.manual-reservation-input:disabled {
            background-color: transparent !important;
            background: transparent !important;
            color: #000000 !important;
          }
        `}
      </style>

      {/* CSV 미리보기 모달 (공용 컴포넌트) */}
      <CsvPreviewModal
        isOpen={csvPreviewModalOpen}
        onClose={() => setCsvPreviewModalOpen(false)}
        data={csvPreviewData}
        onDownloadCsv={handleCsvDownload}
        onDownloadFacture={handleFactureDownload}
        showFactureButton={canExportFacture}
        lang={lang}
        refPrefix="T"
        draftType="tattoo"
        draftYear={selectedYear}
        draftMonth={selectedMonth}
      />

      {/* 하단 여백 - 플로팅메뉴 방지 + PWA safe area */}
      <div style={{ height: 'calc(100px + env(safe-area-inset-bottom, 0px))', minHeight: '100px' }} />

      {/* 스크롤바 숨김 스타일 */}
      <style>
        {`
          .${tableStyles.tableContainer} {
            overflow-x: auto;
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .${tableStyles.tableContainer}::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          
          /* 전체 페이지 컨테이너에도 적용 - 세로 스크롤 허용, 가로 스크롤만 숨김 */
          /* 모바일 PWA 동적 뷰포트 지원: 100vh → 100dvh */
          .${pageContainerStyles.pageContainer} {
            height: 100% !important;
            max-height: 100dvh !important;
            max-height: 100vh !important; /* dvh 미지원 브라우저 폴백 */
            overflow-y: auto !important;
            overflow-x: hidden !important;
            scrollbar-width: thin;
            -ms-overflow-style: auto;
            padding-bottom: env(safe-area-inset-bottom, 0px) !important;
          }
          
          @supports (max-height: 100dvh) {
            .${pageContainerStyles.pageContainer} {
              max-height: 100dvh !important;
            }
          }
          
          .${pageContainerStyles.pageContainer}::-webkit-scrollbar {
            width: 6px;
          }
          
          .${pageContainerStyles.pageContainer}::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          
          .${pageContainerStyles.pageContainer}::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .${pageContainerStyles.pageContainer}::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
    </div >
  );
}

export default ManualReservationList;
